import React, { CSSProperties } from 'react';
import {
  AlertType,
  SwBadge,
  getAlertPropsByType,
  useTooltip,
} from '@kaa/ui-flanders/components';
import { ServiceActiveStatus, ServiceStatus } from '@kaa/api/providers';
import { i18nKeys } from '@kaa/i18n/providers/keys';
import { useTranslation } from 'react-i18next';

export const useStatusBadge = (
  status: ServiceActiveStatus | ServiceStatus,
  remittedVouchers: number,
): JSX.Element | null => {
  const { t } = useTranslation();

  const badgeBase = {
    modMedium: true,
    style: {
      position: 'absolute',
      top: '1.2rem',
      left: '1.2rem',
      width: '2.8rem',
      height: '2.8rem',
    } as CSSProperties,
  };

  const badgeSuccess = {
    ...badgeBase,
    ...getAlertPropsByType(AlertType.SUCCESS),
  };
  const badgeWarning = {
    ...badgeBase,
    ...getAlertPropsByType(AlertType.WARNING),
  };
  const badgeError = { ...badgeBase, ...getAlertPropsByType(AlertType.ERROR) };
  const badgeInformation = {
    ...badgeBase,
    ...getAlertPropsByType(AlertType.INFORMATION),
    modAction: true,
  };

  const toolTipPartiallyAssigned = useTooltip({
    value: t(
      i18nKeys.prestations.search.table.status.text.PARTIALLY_ASSIGNED.tooltip,
    ),
  });

  const toolTipPartiallyRefunded = useTooltip({
    value: t(
      i18nKeys.prestations.search.table.status.text.PARTIALLY_REFUNDED.tooltip,
    ),
  });

  switch (status) {
    case ServiceActiveStatus.PAID:
      return <SwBadge {...badgeSuccess} />;
    case ServiceActiveStatus.MODIFIED:
    case ServiceActiveStatus.CORRECTED:
    case ServiceActiveStatus.CONFIRMED:
    case ServiceActiveStatus.MISSING_VOUCHERS:
    case ServiceActiveStatus.MISSING_CONTRACT:
    case ServiceActiveStatus.PARTIALLY_ASSIGNED_TO_BE_PAID_OUTSIDE_SV:
      return <SwBadge {...badgeWarning} />;
    case ServiceActiveStatus.PARTIALLY_ASSIGNED:
      return (
        <SwBadge
          {...badgeInformation}
          ref={remittedVouchers > 0 ? toolTipPartiallyAssigned : undefined}
        />
      );
    case ServiceActiveStatus.PARTIALLY_REFUNDED:
      return <SwBadge {...badgeWarning} ref={toolTipPartiallyRefunded} />;
    case ServiceActiveStatus.CREATED:
    case ServiceActiveStatus.VALIDATED:
    case ServiceActiveStatus.READY_FOR_VOUCHER_ASSIGNMENT:
      return <SwBadge {...badgeInformation} />;
    case ServiceActiveStatus.PROPOSITION_TO_CANCEL:
    case ServiceActiveStatus.CONTESTED:
      return <SwBadge {...badgeError} />;
    default:
      return null;
  }
};
